
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BottomNav from '../../components/feature/BottomNav';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const services = [
    {
      id: 'flights',
      title: 'حجز طيران',
      subtitle: 'رحلات داخلية وخارجية',
      icon: 'ri-plane-line',
      color: 'bg-blue-500',
      path: '/flights'
    },
    {
      id: 'buses',
      title: 'حجز باصات',
      subtitle: 'رحلات مريحة وآمنة',
      icon: 'ri-bus-line',
      color: 'bg-green-500',
      path: '/buses'
    },
    {
      id: 'hotels',
      title: 'حجز فنادق',
      subtitle: 'إقامة مميزة',
      icon: 'ri-hotel-line',
      color: 'bg-purple-500',
      path: '/hotels'
    },
    {
      id: 'cars',
      title: 'تأجير سيارات',
      subtitle: 'سيارات حديثة',
      icon: 'ri-car-line',
      color: 'bg-orange-500',
      path: '/cars'
    }
  ];

  const offers = [
    {
      title: 'عروض الفنادق الفاخرة',
      subtitle: 'خصم يصل إلى 40%',
      image: 'https://public.readdy.ai/ai/img_res/6e2834ca073234fc17ef5aa543bbe7fb.jpg'
    },
    {
      title: 'رحلات طيران مميزة',
      subtitle: 'أسعار لا تُقاوم',
      image: 'https://readdy.ai/api/search-image?query=Airplane%20cabin%20interior%2C%20comfortable%20seats%2C%20modern%20aviation%20design%2C%20professional%20travel%20photography%2C%20high%20quality%2C%20clean%20and%20spacious%2C%20centered%20composition&width=350&height=180&seq=7&orientation=landscape'
    }
  ];

  const handleServiceClick = (path: string) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 bg-white shadow-sm z-40">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-3 space-x-reverse">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm" style={{ fontFamily: 'Pacifico, serif' }}>
                فَنَار
              </span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-800">مرحباً بك</h1>
              <p className="text-sm text-gray-600">أحمد محمد</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 space-x-reverse">
            <button className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <i className="ri-notification-line text-gray-600"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="pt-20 px-4">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="ابحث عن المدن أو الخدمات..."
              className="w-full bg-white rounded-xl px-4 py-3 pr-12 shadow-sm border border-gray-200 text-sm"
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
              <i className="ri-search-line text-gray-400"></i>
            </div>
          </div>
        </div>

        {/* Offers Slider */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-800 mb-4">العروض المميزة</h2>
          <div className="flex space-x-4 space-x-reverse overflow-x-auto pb-2">
            {offers.map((offer, index) => (
              <div key={index} className="flex-shrink-0 w-80 bg-white rounded-xl overflow-hidden shadow-sm">
                <div className="relative h-32">
                  <img
                    src={offer.image}
                    alt={offer.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 right-3 text-white">
                    <h3 className="font-bold text-sm">{offer.title}</h3>
                    <p className="text-xs opacity-90">{offer.subtitle}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          {services.map((service) => (
            <div
              key={service.id}
              onClick={() => handleServiceClick(service.path)}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
            >
              <div className={`w-12 h-12 ${service.color} rounded-xl flex items-center justify-center mb-4`}>
                <i className={`${service.icon} text-white text-xl`}></i>
              </div>
              <h3 className="font-bold text-gray-800 mb-1">{service.title}</h3>
              <p className="text-sm text-gray-600">{service.subtitle}</p>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => navigate('/my-trips')}
            className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center space-x-3 space-x-reverse"
          >
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <i className="ri-suitcase-fill text-white"></i>
            </div>
            <div className="text-right">
              <h3 className="font-bold text-blue-800">رحلاتي</h3>
              <p className="text-sm text-blue-600">3 حجوزات نشطة</p>
            </div>
          </button>
          
          <button className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3 space-x-reverse">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              <i className="ri-customer-service-fill text-white"></i>
            </div>
            <div className="text-right">
              <h3 className="font-bold text-green-800">الدعم</h3>
              <p className="text-sm text-green-600">تواصل معنا</p>
            </div>
          </button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default HomePage;